/**
 * Entity definition extractors.
 *
 * Shared utilities for extracting metadata from EntityDefinitions and ModuleManifests.
 */

import type { EntityDefinition, ModuleManifest, PluginManifest } from '@gzl10/nexus-sdk'

/** Generic interface for items with dependencies */
interface WithDependencies {
  name: string
  dependencies?: string[]
  optionalDependencies?: string[]
}

/** Entity types that have a database table */
const TYPES_WITH_TABLE = new Set(['collection', 'reference', 'event', 'config', 'temp', 'view', undefined])

/**
 * Extracts table name and CASL subject from an entity definition.
 *
 * Tables are only present for persistent entity types:
 * collection, reference, event, config, temp, view.
 *
 * Subject is optional and must be explicitly defined in casl.subject.
 * Computed, action, external, and virtual entities don't have tables.
 */
export function getTableAndSubject(def: EntityDefinition): { table?: string; subject?: string } {
  const caslSubject = (def as { casl?: { subject?: string } }).casl?.subject

  if (!TYPES_WITH_TABLE.has(def.type)) {
    // computed, action, external, virtual no tienen tabla
    return { subject: caslSubject }
  }

  const table = (def as { table: string }).table
  return { table, subject: caslSubject }
}

/**
 * Generic topological sort using Kahn's algorithm.
 * Items with no dependencies come first, then items that depend on them, etc.
 *
 * @param items - Items to sort
 * @param entityType - Name for error messages ('module' or 'plugin')
 * @throws Error if circular dependencies are detected
 */
function topologicalSortGeneric<T extends WithDependencies>(
  items: T[],
  entityType: string,
  keyFn: (item: T) => string = (item) => item.name
): T[] {
  const itemMap = new Map(items.map(i => [keyFn(i), i]))
  const inDegree = new Map<string, number>()
  const adjList = new Map<string, string[]>()

  // Initialize
  for (const item of items) {
    const key = keyFn(item)
    inDegree.set(key, 0)
    adjList.set(key, [])
  }

  // Build graph: for each dependency, add edge dep -> item
  for (const item of items) {
    const key = keyFn(item)
    for (const dep of [...(item.dependencies ?? []), ...(item.optionalDependencies ?? [])]) {
      if (itemMap.has(dep)) {
        adjList.get(dep)!.push(key)
        inDegree.set(key, (inDegree.get(key) ?? 0) + 1)
      }
    }
  }

  // Start with items that have no dependencies
  const queue = items
    .filter(i => inDegree.get(keyFn(i)) === 0)
    .map(i => keyFn(i))

  const sorted: T[] = []

  while (queue.length > 0) {
    const key = queue.shift()!
    sorted.push(itemMap.get(key)!)

    for (const dependent of adjList.get(key) ?? []) {
      const newDegree = (inDegree.get(dependent) ?? 1) - 1
      inDegree.set(dependent, newDegree)
      if (newDegree === 0) {
        queue.push(dependent)
      }
    }
  }

  if (sorted.length !== items.length) {
    const remaining = items.filter(i => !sorted.includes(i)).map(i => keyFn(i))
    throw new Error(`Circular dependency detected in ${entityType}s: ${remaining.join(', ')}`)
  }

  return sorted
}

/**
 * Sorts modules by their dependencies using Kahn's algorithm (topological sort).
 * Modules with no dependencies come first, then modules that depend on them, etc.
 *
 * @throws Error if circular dependencies are detected
 */
export function topologicalSort(modules: ModuleManifest[]): ModuleManifest[] {
  return topologicalSortGeneric(modules, 'module')
}

/**
 * Sorts plugins by their dependencies using Kahn's algorithm (topological sort).
 * Plugins with no dependencies come first, then plugins that depend on them, etc.
 *
 * @throws Error if circular dependencies are detected
 */
export function topologicalSortPlugins(plugins: PluginManifest[]): PluginManifest[] {
  return topologicalSortGeneric(plugins, 'plugin')
}

/**
 * Validates that all hard dependencies exist in the module list.
 * Optional dependencies are not validated.
 *
 * @throws Error if any hard dependency is missing
 */
export function validateModuleDependencies(modules: ModuleManifest[]): void {
  const names = new Set(modules.map(m => m.name))
  const errors: string[] = []

  for (const mod of modules) {
    for (const dep of mod.dependencies ?? []) {
      if (!names.has(dep)) {
        errors.push(`Module "${mod.name}" requires "${dep}" but it is not registered`)
      }
    }
  }

  if (errors.length > 0) {
    throw new Error(`Missing dependencies:\n  - ${errors.join('\n  - ')}`)
  }
}

/**
 * Validates that all hard dependencies exist in the plugin list.
 *
 * @throws Error if any hard dependency is missing
 */
export function validatePluginDependencies(plugins: PluginManifest[]): void {
  const names = new Set(plugins.map(p => p.name))
  const errors: string[] = []

  for (const plugin of plugins) {
    for (const dep of plugin.dependencies ?? []) {
      if (!names.has(dep)) {
        errors.push(`Plugin "${plugin.name}" requires "${dep}" but it is not registered`)
      }
    }
  }

  if (errors.length > 0) {
    throw new Error(`Missing plugin dependencies:\n  - ${errors.join('\n  - ')}`)
  }
}
